import { useEffect, useRef } from "react";
import type { UseFormReturnType } from "@mantine/form";

const THAANA_SELECTOR = ".thaana-input";

type ThaanaKeyboardCtor = new (selector: string, autoStart?: boolean) => unknown;

/** Sync DOM values from Thaana inputs into Mantine form state (call before validate/submit). */
export function syncThaanaFieldsFromDom<T extends object>(form: UseFormReturnType<T>) {
  collectThaanaInputs().forEach((inputElement) => {
    const fieldName = inputElement.getAttribute("name");
    if (!fieldName) return;
    if (!Object.prototype.hasOwnProperty.call(form.values, fieldName)) return;
    const currentValue = inputElement.value;
    const formValue = form.values[fieldName as keyof T];
    if (currentValue !== formValue) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setFieldValue(fieldName as keyof T, currentValue as any);
    }
  });
}

function collectThaanaInputs(): (HTMLInputElement | HTMLTextAreaElement)[] {
  const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = [];
  document.querySelectorAll(THAANA_SELECTOR).forEach((node) => {
    if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
      inputs.push(node);
      return;
    }
    const nested = node.querySelector("input, textarea");
    if (nested instanceof HTMLInputElement || nested instanceof HTMLTextAreaElement) {
      inputs.push(nested);
    }
  });
  return inputs;
}

/**
 * Thaana keyboard + DOM→React sync (same approach as SingleSourceForm).
 * Wrap Mantine fields in `<motion className="thaana-input">` or set `classNames={{ input: 'thaana-input' }}`.
 */
export function useThaanaFormSync<T extends object>(
  form: UseFormReturnType<T>,
  options?: { autoResizeSelector?: string; enabled?: boolean }
) {
  const formRef = useRef(form);
  formRef.current = form;
  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    void import("thaana-keyboard/ThaanaKeyboard.js").then(() => {
      if (cancelled) return;

      const ThaanaKeyboard = (window as Window & { ThaanaKeyboard?: ThaanaKeyboardCtor }).ThaanaKeyboard;
      if (!ThaanaKeyboard) return;

      new ThaanaKeyboard(THAANA_SELECTOR);

      const syncThaanaField = (inputElement: HTMLInputElement | HTMLTextAreaElement) => {
        const fieldName = inputElement.getAttribute("name");
        if (!fieldName) return;

        const currentForm = formRef.current;
        if (!currentForm?.values || !Object.prototype.hasOwnProperty.call(currentForm.values, fieldName)) {
          return;
        }

        const currentValue = inputElement.value;
        const formValue = currentForm.values[fieldName as keyof T];
        if (currentValue !== formValue) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          currentForm.setFieldValue(fieldName as keyof T, currentValue as any);
        }
      };

      const setupThaanaListeners = () => {
        collectThaanaInputs().forEach((input) => {
          const handleInput = () => syncThaanaField(input);
          const handleBlur = () => syncThaanaField(input);
          input.addEventListener("input", handleInput, true);
          input.addEventListener("blur", handleBlur, true);
          (input as HTMLInputElement & { __thaanaHandlers?: { handleInput: () => void; handleBlur: () => void } }).__thaanaHandlers = {
            handleInput,
            handleBlur,
          };
        });
      };

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "characterData" || mutation.type === "childList") {
            const target = mutation.target as HTMLElement;
            const input = target.closest(THAANA_SELECTOR)?.querySelector("input, textarea") as
              | HTMLInputElement
              | HTMLTextAreaElement
              | undefined;
            const direct =
              target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
                ? target
                : input;
            if (direct) {
              clearTimeout((direct as HTMLInputElement & { syncTimeout?: number }).syncTimeout);
              (direct as HTMLInputElement & { syncTimeout?: number }).syncTimeout = window.setTimeout(
                () => syncThaanaField(direct),
                10
              );
            }
          }
        });
      });

      document.querySelectorAll(THAANA_SELECTOR).forEach((container) => {
        observer.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: false,
        });
      });

      window.setTimeout(setupThaanaListeners, 200);

      cleanup = () => {
        observer.disconnect();
        collectThaanaInputs().forEach((input) => {
          clearTimeout((input as HTMLInputElement & { syncTimeout?: number }).syncTimeout);
          const handlers = (input as HTMLInputElement & { __thaanaHandlers?: { handleInput: () => void; handleBlur: () => void } })
            .__thaanaHandlers;
          if (handlers) {
            input.removeEventListener("input", handlers.handleInput, true);
            input.removeEventListener("blur", handlers.handleBlur, true);
          }
        });
      };
    }).catch((err) => {
      console.error("Failed to load Thaana keyboard:", err);
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !options?.autoResizeSelector) return;
    const resize = () => {
      document.querySelectorAll(`${options.autoResizeSelector} textarea`).forEach((textarea) => {
        const el = textarea as HTMLTextAreaElement;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      });
    };
    const t = window.setTimeout(resize, 100);
    return () => window.clearTimeout(t);
  }, [enabled, options?.autoResizeSelector]);
}
